import { HashRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from './i18n';
import { fetchMasterLocale, hydrateMasterLocaleFromCache, getLanguages } from './redux/translationSlice';
import { fetchFeatureFlags, isQueueScopingOn } from './redux/featureSlice';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute.jsx';
import NotFound from './components/NotFound.jsx';
import NetworkErrorPage from './components/NetworkErrorPage.jsx';
import ServerErrorPage from './components/ServerErrorPage.jsx';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ActivateAccount from './pages/ActivateAccount.jsx';
import './i18n';
import AdminProfile from './pages/profile/AdminProfile.jsx';
import { ErrorProvider, useError, ERROR_TYPES } from './contexts/ErrorContext.jsx';
import { setGlobalErrorHandler } from './hooks/useAxiosAuth.js';
import { SupportSocketProvider } from './contexts/SupportSocketContext.jsx';
import { isSessionMismatch, forceLogout } from './utils/sessionGuard.js';
import Speciality from './pages/master/Speciality.jsx';
import HealthConcern from './pages/master/HealthConcern.jsx';
import SpecialityIcons from './pages/master/SpecialityIcons.jsx';
import MasterSupport from './pages/master/Support.jsx';
import Translation from './pages/master/Translation.jsx';
import Country from './pages/master/Country.jsx';
import Profession from './pages/master/Profession.jsx';
import SpecialityCategory from './pages/master/SpecialityCategory.jsx';
import MasterCatalog from './pages/master/MasterCatalog.jsx';
import CatalogProposals from './pages/master/CatalogProposals.jsx';
import TenantType from './pages/master/TenantType.jsx';
import FeaturePrice from './pages/master/FeaturePrice.jsx';
import AuditLogs from './pages/audit/AuditLogs.jsx';
import AiLog from './pages/audit/AiLog.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import SuperAdminRoute from './components/SuperAdminRoute.jsx';
import PlatformPermissionRoute from './components/PlatformPermissionRoute.jsx';
import Admin from './pages/Admin.jsx';
import TenantManagement from './pages/TenantManagement.jsx';
import TenantForm from './pages/TenantForm.jsx';
import TenantView from './pages/TenantView.jsx';
import SupportQueue from './pages/support/SupportQueue.jsx';
import NoAccess from './pages/NoAccess.jsx';
import { firstAllowedRoute } from './utils/platformPermissions.js';
import Settings from './pages/Settings.jsx';
import MocPatients from './pages/moc/MocPatients.jsx';
import MocPatientDetail from './pages/moc/MocPatientDetail.jsx';
import ServiceVisibility from './pages/moc/ServiceVisibility.jsx';
import PatientInvite from './pages/patients/PatientInvite.jsx';
import ReportsPatients from './pages/reports/ReportsPatients.jsx';
import ReportsCount from './pages/reports/ReportsCount.jsx';
import PlatformUsers from './pages/platform/PlatformUsers.jsx';
import PlatformRoles from './pages/platform/PlatformRoles.jsx';
import PlatformDashboard from './pages/dashboard/PlatformDashboard.jsx';
import ReportsTenants from './pages/reports/ReportsTenants.jsx';
import ReportsTenantCount from './pages/reports/ReportsTenantCount.jsx';
import EmailQueue from './pages/audit/EmailQueue.jsx';
import ContactInquiries from './pages/ContactInquiries.jsx';
import TenantPlan from './pages/tenantBilling/TenantPlan.jsx';

// The Mitra One Care list moved to /patients when the owner merged the two
// patient entries (Aug 2026). Old detail links carry the account id in the
// path, so a plain <Navigate> can't rewrite them — this one-liner can.
function LegacyMocPatientRedirect() {
  const { mocAccountId } = useParams();
  return <Navigate to={`/patients/${mocAccountId}`} replace />;
}

// Where "/" goes. Evaluated at RENDER time — as a component, not a value
// computed once in AppContent. AppContent mounts before anyone is signed in
// and never re-renders on login, so a value captured there would still hold
// <Navigate to="/login"> after a same-tab login; Login would then see the
// authed session and send the user back to "/" — an infinite ping-pong in
// which platform staff never reach their queue.
//
// Two personas. Super Admin's home is the platform dashboard. PLATFORM STAFF
// (the Super Admin's own team) land on the first page their role lets them
// open — a support queue when they hold one, else the first sidebar entry in
// rail order (dashboard when the role holds reports.view, then tenants,
// patients, masters, audit, team) — or a plain "no access"
// page when it grants none, rather than a redirect loop. Anyone else is sent
// to /login, where the Login page explains this is the Super Admin portal and
// signs them out.
function DefaultLanding() {
  const authed = localStorage.getItem('isAuthenticated') === 'true';
  const role = localStorage.getItem('Role');
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  // Whether support.queue.<queue> still counts (Settings → Support → "Queue-
  // based agent access"). Taken from the store rather than the localStorage
  // mirror: logout wipes localStorage, the store survives a same-tab re-login.
  const queueScopingOn = useSelector(isQueueScopingOn);

  if (authed && role === 'admin' && isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  if (authed && role === 'platform_staff') {
    const firstRoute = firstAllowedRoute(queueScopingOn);
    return firstRoute ? <Navigate to={firstRoute} replace /> : <NoAccess />;
  }
  return <Navigate to="/login" replace />;
}

function AppContent() {
  const { errorType, setNetworkError, setServerError, clearError } = useError();
  const dispatch = useDispatch();

  // Set up global error handler for axios
  useEffect(() => {
    setGlobalErrorHandler({ setNetworkError, setServerError, clearError });
  }, [setNetworkError, setServerError, clearError]);

  // Master-data translation bootstrap.
  // 1. Fetch the language master once so the header language popup has the dynamic list.
  // 2. Hydrate the active locale synchronously from localStorage so first render uses
  //    cached labels (stale-while-revalidate — instant render, no flash).
  // 3. Fire a background fetch to refresh the cache.
  // 4. On every i18n language change, hydrate + refetch for the new language.
  // 5. On login (fires a `login` window event), re-run the fetch — logout calls
  //    localStorage.clear() which wipes the master-locale cache, so a fresh fetch
  //    is required once the user is authenticated again.
  useEffect(() => {
    const currentLcode = () =>
      i18n.language || localStorage.getItem('i18nextLng') || 'en';

    const loadLocale = (lcode) => {
      if (!lcode) return;
      dispatch(hydrateMasterLocaleFromCache(lcode));
      dispatch(fetchMasterLocale({ lcode }));
    };

    const refresh = () => {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
      loadLocale(currentLcode());
    };

    // Initial load on app mount.
    refresh();
    // Feature flags load even before login (the /feature/by-keys route is
    // unauthenticated); the post-login layout skips re-fetching when
    // state.feature.loaded is already true.
    dispatch(fetchFeatureFlags());

    const handleLangChange = (lng) => loadLocale(lng);
    const handleLogin = () => refresh();
    const handleLogout = () => {
      // Re-seed with the current language so the public /login page still
      // renders translated labels after the logout wipe.
      loadLocale(currentLcode());
    };

    i18n.on('languageChanged', handleLangChange);
    window.addEventListener('login', handleLogin);
    window.addEventListener('logout', handleLogout);
    return () => {
      i18n.off('languageChanged', handleLangChange);
      window.removeEventListener('login', handleLogin);
      window.removeEventListener('logout', handleLogout);
    };
  }, [dispatch]);

  // Cross-tab session guard. Catches the case where another tab in the same
  // browser logs in or out as a different user — if it does, this tab's
  // sessionStorage id no longer matches localStorage's activeSessionId and
  // we exile this tab to /login. forceLogout is idempotent, so we don't need
  // a latch — repeated triggers from focus/storage events while we're already
  // on /login are no-ops.
  useEffect(() => {
    const checkSession = () => {
      if (isSessionMismatch()) forceLogout();
    };

    const onStorage = (e) => {
      // Only react to keys that signal an auth change.
      if (
        e.key === 'activeSessionId' ||
        e.key === 'accessToken' ||
        e.key === 'ID' ||
        e.key === null /* localStorage.clear() — key is null */
      ) {
        checkSession();
      }
    };
    const onFocus = () => checkSession();

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Show error pages when network or server errors occur
  if (errorType === ERROR_TYPES.NETWORK) {
    return <NetworkErrorPage />;
  }

  if (errorType === ERROR_TYPES.SERVER) {
    return <ServerErrorPage />;
  }

  return (
    <Router data-testid="app-router">
      <Routes data-testid="app-routes">
        {/* Public Routes - Always Accessible */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/activate" element={<ActivateAccount />} />
        {/* Protected Routes - Require Authentication */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppLayout />}>
            {/* Default Dashboard Route */}
            <Route path="/" element={<DefaultLanding />} />

            {/* ---- Every page below opens by PERMISSION, not by role ----
                RoleRoute admits the two personas this portal signs in: the
                Super Admin (Role 'admin' + isSuperAdmin, normalised at login)
                and their PLATFORM STAFF (Role 'platform_staff'). Inside, each
                page family sits under PlatformPermissionRoute with the
                catalogue key the API checks for the same calls
                (Backend/config/platformPermissions.js): the Super Admin passes
                every check unconditionally, a staff member passes only what
                their role grants. This list and the sidebar manifest
                (config/platformNav.js) must agree, or a link opens a page that
                bounces — the bug this layout replaced: only the support queues
                were wired for staff, so a role holding every permission still
                saw two entries. */}
            <Route element={<RoleRoute allow={['admin', 'platform_staff']} />}>
              {/* ---- Super-admin ONLY — no catalogue key exists for these ----
                  Settings holds the platform feature flags and payment holds
                  (requireSuperAdmin on the API). Kept behind an explicit
                  SuperAdminRoute now that RoleRoute admits platform staff. */}
              <Route element={<SuperAdminRoute />}>
                <Route path="settings" element={<Settings />} />
                {/* Outbound mail queue — an ops screen (requireSuperAdmin on
                    the API), listed under Audit & Security in the rail. */}
                <Route path="email-queue" element={<EmailQueue />} />
                {/* Custom-plan contact requests from the public sites and from
                    doctors. Sales leads about the PLATFORM, so the API is
                    super_admin only (a tenant admin reading them would see
                    every other hospital's) — there is no catalogue key to gate
                    it with, which is why it sits here and not with the tenant
                    pages it is listed beside in the rail. */}
                <Route path="contact-inquiries" element={<ContactInquiries />} />
                {/* Plan Requests (doctor plan payments across every tenant)
                    was removed on 5 Oct 2026 (doc/5.10.2026.md): a doctor's
                    plan payment belongs to that tenant's admin (Doctor plans
                    → Payments → Mark paid & activate). */}
              </Route>

              {/* ---- Platform reports (reports.view) ----
                  The Super Admin's own numbers, read from /api/platform/
                  reports/*: the landing dashboard, the tenant report and the
                  tenant count. One key for the three — a role that may see
                  one platform-wide figure may see them all. The patient
                  report / count stay under patients.view below. */}
              <Route element={<PlatformPermissionRoute perms={['reports.view']} />}>
                <Route path="dashboard" element={<PlatformDashboard />} />
                <Route path="tenant-report" element={<ReportsTenants />} />
                <Route path="tenant-count" element={<ReportsTenantCount />} />
              </Route>

              {/* ---- Support queues ----
                  Every ticket on the platform lands on this side (no tenant-
                  admin tier any more): "Tenant Support" holds what hospital
                  admins, doctors, staff and isolated-tenant patients raise;
                  "Patient Support" holds the Mitra One Care patient app's. A
                  staff member needs support.view plus the queue's own
                  permission, so a Patient-only agent cannot deep-link into the
                  tenant queue. The socket re-checks both.

                  Keyed per queue so switching between the two remounts the
                  page (fresh lists, fresh room) instead of reusing one
                  instance. */}
              <Route element={<PlatformPermissionRoute perms={['support.view', 'support.queue.tenant']} />}>
                <Route path="platform-support" element={<SupportQueue key="tenant" queue="tenant" />} />
              </Route>
              <Route element={<PlatformPermissionRoute perms={['support.view', 'support.queue.patient']} />}>
                <Route path="moc/support" element={<SupportQueue key="patient" queue="patient" />} />
              </Route>

              {/* ---- Tenants (tenants.view to look, tenants.manage to change) ---- */}
              <Route element={<PlatformPermissionRoute perms={['tenants.view']} />}>
                <Route path="tenants" element={<TenantManagement />} />
                {/* Read-only summary opened from the list's eye icon — details,
                    services, enabled features and the recorded plan value in
                    one place, without the risk of an accidental edit. */}
                <Route path="tenants/:tenantId/view" element={<TenantView />} />
                {/* The tenant's own plan from the platform (doc/5.10.2026.md):
                    current plan, renew / change, invoices, Mark paid. Reading
                    needs tenants.view; the page hides the writes without
                    tenants.manage, and the API refuses them. */}
                <Route path="tenants/:tenantId/plan" element={<TenantPlan />} />
                {/* The admins who run the tenants. The list is a read; Add /
                    delete / status on the page need tenants.manage. */}
                <Route path="admins" element={<Admin />} />
                {/* Where the Admins list's eye icon opens one admin (id via
                    route state). Without state it shows the signed-in
                    admins-table row — the Super Admin's own profile; platform
                    staff have no such row (theirs is /platform-staff/me). */}
                <Route path="manageAdminProfile" element={<AdminProfile />} />
              </Route>
              <Route element={<PlatformPermissionRoute perms={['tenants.manage']} />}>
                {/* Tenant create/edit is a full PAGE (no modal) — decision of
                    20 Aug 2026; back button returns to the list. */}
                <Route path="tenants/new" element={<TenantForm />} />
                <Route path="tenants/:tenantId/edit" element={<TenantForm />} />
                {/* Per-feature price list behind the plan quote: internal
                    commercial data, read-gated on the API to tenants.manage
                    (not masters.view), so it lives here rather than with the
                    other masters. */}
                <Route path="feature-price" element={<FeaturePrice />} />
              </Route>

              {/* ---- Mitra One Care (patients.view; writes need patients.manage) ----
                  Platform administration that used to live in the Tenant #1
                  admin app, behind an "is this the open-platform tenant?"
                  guard. Here it needs no such gate; the API re-checks the
                  permission on every call.

                  The MocSection tab-bar shell is gone (owner's call, sidebar
                  restructure of Aug 2026): the sidebar's MOC & PATIENTS
                  section links to each screen directly, so every page renders
                  as a plain route and carries its own page header. The bare
                  /moc path keeps the old index behaviour as a redirect. */}
              <Route element={<PlatformPermissionRoute perms={['patients.view']} />}>
                {/* ONE patient list (owner's merge, Aug 2026): "Mitra One
                    Care" and "Patients" showed the same people twice — once
                    per identity, once per hospital record — so the identity
                    list took the name and the route. The per-hospital
                    PatientsAll/PatientView pair is deleted, not parked. */}
                <Route path="patients" element={<MocPatients />} />
                {/* A full page rather than a modal: the activity trail pages
                    independently and break-glass opens a dialog on top of it. */}
                <Route path="patients/:mocAccountId" element={<MocPatientDetail />} />
                <Route path="moc/services" element={<ServiceVisibility />} />
                {/* moc/support (Patient Support) lives above, under its own
                    support permissions. */}
                {/* Every path the merged list replaced, kept as redirects so a
                    bookmark or an already-open tab lands on the new screen
                    instead of the 404. */}
                <Route path="moc" element={<Navigate to="/patients" replace />} />
                <Route path="moc/patients" element={<Navigate to="/patients" replace />} />
                <Route path="moc/patients/:mocAccountId" element={<LegacyMocPatientRedirect />} />
                <Route path="moc-patients" element={<Navigate to="/patients" replace />} />
                <Route path="patient-report" element={<ReportsPatients />} />
                <Route path="patient-count" element={<ReportsCount />} />
              </Route>
              <Route element={<PlatformPermissionRoute perms={['invites.send']} />}>
                <Route path="patient-invite" element={<PatientInvite />} />
              </Route>

              {/* ---- Platform team (each screen has its own key) ---- */}
              <Route element={<PlatformPermissionRoute perms={['users.manage']} />}>
                <Route path="users" element={<PlatformUsers />} />
              </Route>
              <Route element={<PlatformPermissionRoute perms={['roles.manage']} />}>
                <Route path="user-roles" element={<PlatformRoles />} />
              </Route>

              {/* ---- Master data (masters.view; writes need masters.manage) ----
                  GLOBAL catalogues shared by every hospital, so only the
                  platform side may manage them. */}
              <Route element={<PlatformPermissionRoute perms={['masters.view']} />}>
                <Route path="translation" element={<Translation />} />
                <Route path="speciality-category" element={<SpecialityCategory />} />
                <Route path="speciality" element={<Speciality />} />
                {/* The patient-language layer in front of the specialty
                    catalogue — "Fever, cold & cough" -> General Medicine. It
                    feeds the "Consult by concern" chips on the patient home,
                    so it is a GLOBAL master like the two above it. */}
                <Route path="master/health-concerns" element={<HealthConcern />} />
                {/* The bundled icon library those two masters pick from — a
                    read-only gallery, so it has no manage key. */}
                <Route path="speciality-icons" element={<SpecialityIcons />} />
                {/* World-level item catalogs (Phase 2) — medicine / lab_test /
                    imaging_procedure share ONE registry-driven page, and the
                    proposals queue reviews what hospitals send up. */}
                <Route path="master-catalog/:kind" element={<MasterCatalog />} />
                <Route path="catalog-proposals" element={<CatalogProposals />} />
                <Route path="master-support" element={<MasterSupport />} />
                <Route path="country" element={<Country />} />
                <Route path="profession" element={<Profession />} />
                {/* Tenant types: the grouped presets the creation form offers. */}
                <Route path="tenant-type" element={<TenantType />} />
              </Route>

              {/* ---- Audit & Security (audit.view; append-only, no write key) ---- */}
              <Route element={<PlatformPermissionRoute perms={['audit.view']} />}>
                <Route path="audit/api" element={<AuditLogs category="api" title="API Logs" description="Every API request — who, what, where, result, duration." />} />
                <Route path="audit/socket" element={<AuditLogs category="socket" title="Socket Logs" description="Real-time Socket.IO events — connections, chat, support, dashboard." />} />
                <Route path="audit/login" element={<AuditLogs category="login" title="Login Logs" description="Authentication events — login, logout, failed attempts." />} />
                <Route path="audit/activity" element={<AuditLogs category="activity" title="Activity Logs" description="Data changes — create / update / delete on records." />} />
                <Route path="audit/appointments" element={<AuditLogs category="appointment" title="Appointment Logs" description="Appointment lifecycle — booked, rescheduled, cancelled, completed, no-show." />} />
                <Route path="audit/security" element={<AuditLogs category="security" title="Security Monitoring" description="Security events — suspicious activity, blocked access." />} />
                <Route path="audit/ai" element={<AiLog />} />
              </Route>
            </Route>
          </Route>
        </Route>

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Router>
  );
}

function App() {
  return (
    <ErrorProvider>
      <SupportSocketProvider>
        <AppContent />
      </SupportSocketProvider>
    </ErrorProvider>
  );
}

export default App;
